// frontend/src/pages/AdminUsers.jsx
import React, { useState, useEffect } from 'react';
import { userAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  UserIcon,
  PencilIcon,
  KeyIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  LockOpenIcon
} from '@heroicons/react/24/outline';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'user'
  });
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });

  // Load users
  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        search: searchTerm,
        role: roleFilter,
        sort: sortBy,
        order: sortOrder
      };

      const response = await userAPI.getAll(params);
      if (!response.data.error) {
        setUsers(response.data.data.users);
        setTotalPages(response.data.data.pagination.totalPages);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [currentPage, searchTerm, roleFilter, sortBy, sortOrder]);

  // Handle form changes
  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  // Create user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await userAPI.create(formData);
      if (!response.data.error) {
        setSuccess('User created successfully');
        setShowCreateModal(false);
        resetForm();
        loadUsers();
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Failed to create user');
    }
  };

  // Update user
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await userAPI.update(selectedUser.id, formData);
      if (!response.data.error) {
        setSuccess('User updated successfully');
        setShowEditModal(false);
        resetForm();
        loadUsers();
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Failed to update user');
    }
  };

  // Change password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    try {
      const response = await userAPI.changePassword(selectedUser.id, {
        new_password: passwordData.new_password
      });
      if (!response.data.error) {
        setSuccess('Password changed successfully');
        setShowPasswordModal(false);
        setPasswordData({ new_password: '', confirm_password: '' });
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Failed to change password');
    }
  };

  // Helper function to check if user is inactive (handles both boolean false and numeric 0)
  const isUserInactive = (user) => !user.is_active || user.is_active === 0;

  // Toggle user status (disable/enable)
  const handleToggleUserStatus = async () => {
    try {
      const newStatus = isUserInactive(selectedUser) ? true : false;
      console.log('Current user:', selectedUser);
      console.log('Current is_active value:', selectedUser.is_active, 'Type:', typeof selectedUser.is_active);
      console.log('Is user inactive?', isUserInactive(selectedUser));
      console.log('New status will be:', newStatus);
      
      const response = await userAPI.toggleStatus(selectedUser.id, { is_active: newStatus });
      console.log('API response:', response);
      
      if (!response.data.error) {
        setSuccess(`User ${newStatus ? 'enabled' : 'disabled'} successfully`);
        setShowDisableModal(false);
        // Force a complete refresh by clearing users first
        setUsers([]);
        setLoading(true);
        await loadUsers();
      } else {
        console.error('API error:', response.data.message);
        setError(response.data.message);
      }
    } catch (err) {
      console.error('Toggle error:', err);
      setError('Failed to update user status');
    }
  };

  // Modal handlers
  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '', // Don't pre-fill password
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role
    });
    setShowEditModal(true);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setPasswordData({ new_password: '', confirm_password: '' });
    setShowPasswordModal(true);
  };

  const openDisableModal = (user) => {
    setSelectedUser(user);
    setShowDisableModal(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'user'
    });
    setSelectedUser(null);
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowPasswordModal(false);
    setShowDisableModal(false);
    resetForm();
    setPasswordData({ new_password: '', confirm_password: '' });
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Clear messages
  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  if (loading && users.length === 0) {
    return <LoadingSpinner message="Loading users..." />;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
            <UserGroupIcon className="w-6 h-6 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="mr-2 w-4 h-4" />
          Add New User
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="relative px-4 py-3 mb-6 text-red-700 bg-red-50 rounded border border-red-200">
          {error}
          <button onClick={clearMessages} className="absolute top-0 right-0 bottom-0 px-4 py-3">
            <span className="sr-only">Dismiss</span>&times;
          </button>
        </div>
      )}

      {success && (
        <div className="relative px-4 py-3 mb-6 text-green-700 bg-green-50 rounded border border-green-200">
          {success}
          <button onClick={clearMessages} className="absolute top-0 right-0 bottom-0 px-4 py-3">
            <span className="sr-only">Dismiss</span>&times;
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-6 card">
        <div className="pt-6 card-content">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users by email or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="py-2 pr-4 pl-10 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Role Filter */}
            <div className="sm:w-40">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Roles</option>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* Sort By */}
            <div className="sm:w-40">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="created_at">Created Date</option>
                <option value="email">Email</option>
                <option value="first_name">First Name</option>
                <option value="last_name">Last Name</option>
                <option value="total_cost">Total Cost</option>
                <option value="total_batches">Total Batches</option>
              </select>
            </div>

            {/* Sort Order */}
            <div className="sm:w-36">
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="px-3 py-2 pr-8 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="mb-6 card">
        <div className="pt-6 card-content">
          {loading ? (
            <div className="py-8 text-center">
              <LoadingSpinner />
            </div>
          ) : users.length === 0 ? (
            <div className="py-12 text-center">
              <div className="flex justify-center items-center mx-auto mb-4 w-12 h-12 bg-gray-100 rounded-lg">
                <UserIcon className="w-6 h-6 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-medium text-gray-900">No users found</h3>
              <p className="text-gray-500">Try adjusting your search criteria</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 font-medium text-left text-gray-900">User</th>
                    <th className="px-4 py-3 font-medium text-left text-gray-900">Role</th>
                    <th className="px-4 py-3 font-medium text-left text-gray-900">Status</th>
                    <th className="px-4 py-3 font-medium text-left text-gray-900">Last Activity</th>
                    <th className="px-4 py-3 font-medium text-left text-gray-900">Account Created</th>
                    <th className="px-4 py-3 font-medium text-right text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className={`border-b border-gray-100 hover:bg-gray-50 ${isUserInactive(user) ? 'bg-gray-50 opacity-75' : ''}`}>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-10 h-10">
                            <div className="flex justify-center items-center w-10 h-10 bg-gray-100 rounded-full">
                              <UserIcon className="w-5 h-5 text-gray-500" />
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="font-medium text-gray-900">
                              {user.first_name} {user.last_name}
                              {isUserInactive(user) && (
                                <span className="inline-flex px-2 py-1 ml-2 text-xs font-medium text-gray-800 bg-gray-100 rounded-full">
                                  Disabled
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          {user.role === 'admin' ? (
                            <ShieldCheckIcon className="mr-1 w-4 h-4 text-purple-500" />
                          ) : (
                            <UserIcon className="mr-1 w-4 h-4 text-blue-500" />
                          )}
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            user.role === 'admin' 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {user.role}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          {isUserInactive(user) ? (
                            <span className="inline-flex px-2 py-1 text-xs font-medium text-red-800 bg-red-100 rounded-full">
                              Inactive
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                              Active
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {user.last_activity ? formatDate(user.last_activity) : 'Never'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-end items-center space-x-2">
                          <button
                            onClick={() => openEditModal(user)}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md border border-blue-200 hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            title="Edit User"
                          >
                            <PencilIcon className="mr-1 w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => openPasswordModal(user)}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-orange-700 bg-orange-50 rounded-md border border-orange-200 hover:bg-orange-100 hover:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                            title="Change Password"
                          >
                            <KeyIcon className="mr-1 w-4 h-4" />
                            Password
                          </button>
                          <button
                            onClick={() => openDisableModal(user)}
                            className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                              isUserInactive(user)
                                ? 'text-green-700 bg-green-50 border border-green-200 hover:bg-green-100 hover:border-green-300 focus:ring-green-500'
                                : 'text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 hover:border-red-300 focus:ring-red-500'
                            }`}
                            title={isUserInactive(user) ? "Enable User" : "Disable User"}
                          >
                            {isUserInactive(user) ? (
                              <LockOpenIcon className="mr-1 w-4 h-4" />
                            ) : (
                              <LockClosedIcon className="mr-1 w-4 h-4" />
                            )}
                            {isUserInactive(user) ? 'Enable' : 'Disable'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center mb-6 space-x-4">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="mr-1 w-4 h-4" />
            Previous
          </button>
          
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRightIcon className="ml-1 w-4 h-4" />
          </button>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-md bg-white rounded-lg">
            <h2 className="mb-4 text-xl font-bold">Create New User</h2>
            <form onSubmit={handleCreateUser}>
              <div>
                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleFormChange}
                    required
                    minLength="6"
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleFormChange}
                    required
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleFormChange}
                    required
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="mb-6">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Role *
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleFormChange}
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end pt-4 space-x-2">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <UserIcon className="mr-2 w-4 h-4" />
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-md bg-white rounded-lg">
            <h2 className="mb-4 text-xl font-bold">Edit User</h2>
            <form onSubmit={handleUpdateUser}>
              <div>
                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleFormChange}
                    required
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleFormChange}
                    required
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="mb-6">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Role *
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleFormChange}
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end pt-4 space-x-2">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PencilIcon className="mr-2 w-4 h-4" />
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-md bg-white rounded-lg">
            <h2 className="mb-4 text-xl font-bold">
              Change Password for {selectedUser?.email}
            </h2>
            <form onSubmit={handleChangePassword}>
              <div>
                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    New Password *
                  </label>
                  <input
                    type="password"
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    required
                    minLength="6"
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="mb-6">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    name="confirm_password"
                    value={passwordData.confirm_password}
                    onChange={handlePasswordChange}
                    required
                    minLength="6"
                    className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4 space-x-2">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md border border-transparent shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  <KeyIcon className="mr-2 w-4 h-4" />
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Disable/Enable User Modal */}
      {showDisableModal && selectedUser && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-md bg-white rounded-lg">
            <h2 className="mb-4 text-xl font-bold">
              {isUserInactive(selectedUser) ? 'Enable User Account' : 'Disable User Account'}
            </h2>
            <div className="mb-6">
              <p className="text-gray-600">
                {isUserInactive(selectedUser)
                  ? `Are you sure you want to enable "${selectedUser.email}"? They will regain access to their account.`
                  : `Are you sure you want to disable "${selectedUser.email}"? They will lose access to their account but their data will be preserved.`
                }
              </p>
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={closeModals}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button 
                onClick={handleToggleUserStatus}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  isUserInactive(selectedUser)
                    ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                    : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                }`}
              >
                {isUserInactive(selectedUser) ? (
                  <LockOpenIcon className="mr-2 w-4 h-4" />
                ) : (
                  <LockClosedIcon className="mr-2 w-4 h-4" />
                )}
                {isUserInactive(selectedUser) ? 'Enable User' : 'Disable User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;